// What a data page must remember to be put back exactly as it was: the job, its results, the
// areas and the day and time resolution it was run for. Two routes lead back to a map page, the
// in-app Back buttons on the preview and the browser's own Back button, and both must restore
// the same thing. So the page hands the preview one state object (the "hand-off"), and this one
// function turns a hand-off into the state a map page restores from. The preview builds its Back
// state from the hand-off it received; the map page writes the same function's result into its
// own history entry just before it opens the preview. They cannot drift apart.
// Pure: no React, router or Firebase imports.
import type { ActivityMeasure, Granularity } from '../config/products';
import type { JobStatusResponse } from '../services/dataJobs';
import type { OdmDirection } from './odmDirection';
import type { WindowUtc } from './submittedWindow';

/** What the Activities map page hands the Activities preview. */
export interface ActivityHandoff {
  jobId: string;
  jobResults: unknown[];
  jobStatus?: JobStatusResponse | null;
  /** Every area drawn on the map, so Back can redraw it. */
  geometries?: unknown[];
  selectedGrids?: unknown[];
  geoType?: string | null;
  countryCode?: string | null;
  /** The delivery day and resolution the job was run for (not what the picker shows now). */
  day?: string | null;
  granularity?: Granularity | null;
  /** The measure the job was run for, so the preview names its figures after it. */
  measure: ActivityMeasure | null;
  /** The start and end instants the job was submitted with; null on an older hand-off. */
  windowUtc: WindowUtc | null;
  /** The picker's query string (its country filter), so Back from the map lands on the same list. */
  pickerSearch?: string | null;
}

/** The router state the Activities map page restores from (`preserveState`). */
export function activityBackState(h: ActivityHandoff) {
  return {
    preserveState: true as const,
    jobId: h.jobId,
    jobResults: h.jobResults,
    jobStatus: h.jobStatus,
    preservedGeometries: h.geometries,
    preservedSelectedGrids: h.selectedGrids,
    preservedGeoType: h.geoType ?? undefined,
    preservedCountryCode: h.countryCode ?? undefined,
    preservedDay: h.day ?? undefined,
    preservedGranularity: h.granularity ?? undefined,
    preservedMeasure: h.measure ?? undefined,
    preservedWindowUtc: h.windowUtc ?? undefined,
    preservedPickerSearch: h.pickerSearch ?? undefined,
  };
}

/** What the Origin-Destination map page hands the Origin-Destination preview. */
export interface OdmHandoff {
  jobId: string;
  jobResults: unknown[];
  jobStatus?: JobStatusResponse | null;
  /** The areas picked on the setup page (all of one area type) and how trips are counted for them. */
  selection?: unknown[];
  geoType?: string | null;
  direction?: OdmDirection | null;
  /** The selection as drawn on the map, so Back can redraw it. */
  geometries?: unknown[];
  countryCode?: string | null;
  /** The delivery day and resolution the job was run for (not what the picker shows now). */
  preservedDay?: string | null;
  preservedGranularity?: Granularity | null;
  /** The start and end instants the job was submitted with; null on an older hand-off. */
  windowUtc: WindowUtc | null;
  /** The picker's query string (its country filter), so Back from the map lands on the same list. */
  pickerSearch?: string | null;
}

/** The router state the Origin-Destination map page restores from (`preserveState`). */
export function odmBackState(h: OdmHandoff) {
  return {
    preserveState: true as const,
    jobId: h.jobId,
    jobResults: h.jobResults,
    jobStatus: h.jobStatus,
    selection: h.selection,
    geoType: h.geoType ?? undefined,
    direction: h.direction ?? undefined,
    geometries: h.geometries,
    countryCode: h.countryCode ?? undefined,
    preservedDay: h.preservedDay ?? undefined,
    preservedGranularity: h.preservedGranularity ?? undefined,
    preservedWindowUtc: h.windowUtc ?? undefined,
    pickerSearch: h.pickerSearch ?? undefined,
  };
}
